import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface TooltipProps {
  /** full text shown in the hover bubble */
  content: string;
  children: React.ReactNode;
  /** show the bubble only when the wrapped content is actually truncated */
  onlyWhenTruncated?: boolean;
  className?: string;
}

/**
 * Lightweight hover tooltip rendered through a portal so it is not clipped
 * by overflow containers (e.g. horizontally scrollable tables).
 *
 * The bubble takes its width from the content (capped at max-w-xs) — a fixed box
 * with only `left` set would otherwise shrink to the space between the anchor and
 * the viewport's right edge and wrap into a one-word-wide column next to right-
 * aligned buttons. It is then shifted back inside the viewport while the arrow
 * stays centred over the anchor.
 */
const VIEWPORT_MARGIN = 8;

export function Tooltip({ content, children, onlyWhenTruncated = false, className }: TooltipProps) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  // Horizontal shift (px) keeping the bubble inside the viewport; the arrow undoes it.
  const [shift, setShift] = useState(0);

  useLayoutEffect(() => {
    const bubble = bubbleRef.current;
    if (!pos || !bubble) return;
    const half = bubble.getBoundingClientRect().width / 2;
    const left = pos.left - half;
    const right = pos.left + half;
    let dx = 0;
    if (left < VIEWPORT_MARGIN) dx = VIEWPORT_MARGIN - left;
    else if (right > window.innerWidth - VIEWPORT_MARGIN) dx = window.innerWidth - VIEWPORT_MARGIN - right;
    setShift(Math.round(dx));
  }, [pos]);

  const show = () => {
    timerRef.current = setTimeout(() => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      if (onlyWhenTruncated) {
        const el = wrapper.firstElementChild ?? wrapper;
        if (el.scrollHeight <= el.clientHeight && el.scrollWidth <= el.clientWidth) return;
      }
      const r = wrapper.getBoundingClientRect();
      setPos({ top: r.bottom + 6, left: r.left + r.width / 2 });
    }, 300);
  };

  const hide = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setPos(null);
    setShift(0);
  };

  return (
    <>
      <span ref={wrapperRef} className={className} onMouseEnter={show} onMouseLeave={hide}>
        {children}
      </span>

      {pos &&
        createPortal(
          <div
            ref={bubbleRef}
            className="pointer-events-none fixed z-[9999] w-max max-w-xs flex flex-col items-center"
            style={{ top: pos.top, left: pos.left, transform: `translateX(calc(-50% + ${shift}px))` }}
          >
            {/* arrow pointing up — stays over the anchor even when the bubble is shifted */}
            <div
              className="w-0 h-0 border-l-[6px] border-r-[6px] border-b-[6px] border-l-transparent border-r-transparent border-b-gray-900 dark:border-b-gray-700"
              style={{ transform: `translateX(${-shift}px)` }}
            />
            <div className="max-w-xs rounded-lg bg-gray-900 dark:bg-gray-700 px-3 py-2 text-xs text-white shadow-xl whitespace-pre-wrap break-words">
              {content}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
